import { PDFDocument } from 'pdf-lib';

/**
 * Parse page range string into an array of page numbers
 * @param pageRange - String like "1-3,5,7-9"
 * @returns Array of page numbers (1-indexed)
 */
export const parsePageRange = (pageRange: string): number[] => {
  if (!pageRange.trim()) return [];

  const pages: number[] = [];
  const parts = pageRange.split(',').map(part => part.trim());

  for (const part of parts) {
    if (part.includes('-')) {
      const [start, end] = part.split('-').map(num => parseInt(num.trim(), 10));
      if (!isNaN(start) && !isNaN(end) && start <= end) {
        for (let i = start; i <= end; i++) {
          pages.push(i);
        }
      }
    } else {
      const pageNum = parseInt(part, 10);
      if (!isNaN(pageNum)) {
        pages.push(pageNum);
      }
    }
  }

  // Remove duplicates and sort
  return Array.from(new Set(pages)).sort((a, b) => a - b);
};

/**
 * Split PDF based on page range
 * @param file - Original PDF file
 * @param pageRange - String like "1-3,5,7-9" or empty for all pages
 * @returns Promise<File> - New PDF file with selected pages
 */
export const splitPDF = async (file: File, pageRange: string): Promise<File> => {
  try {
    // Read the original PDF
    const arrayBuffer = await file.arrayBuffer();
    const pdfDoc = await PDFDocument.load(arrayBuffer);

    // If no page range specified, return original file
    if (!pageRange.trim()) {
      return file;
    }

    // Parse page range
    const selectedPages = parsePageRange(pageRange);

    if (selectedPages.length === 0) {
      return file;
    }

    // Create new PDF document
    const newPdfDoc = await PDFDocument.create();

    // Get total pages in original document
    const totalPages = pdfDoc.getPageCount();

    // Copy selected pages to new document
    for (const pageNum of selectedPages) {
      // Convert to 0-indexed
      const pageIndex = pageNum - 1;

      // Skip if page number is out of range
      if (pageIndex < 0 || pageIndex >= totalPages) {
        continue;
      }

      // Copy the page
      const [copiedPage] = await newPdfDoc.copyPages(pdfDoc, [pageIndex]);
      newPdfDoc.addPage(copiedPage);
    }

    // Generate PDF bytes
    const pdfBytes = await newPdfDoc.save();

    // Create new file with split content
    const splitFileName = `${file.name.replace('.pdf', '')}_(pages_${formatPageRangeForDisplay(pageRange)}).pdf`;
    const splitFile = new File([pdfBytes], splitFileName, { type: 'application/pdf' });

    return splitFile;
  } catch (error) {
    console.error('Error splitting PDF:', error);
    throw new Error('Failed to split PDF. Please check the page range and try again.');
  }
};

/**
 * Get the total number of pages in a PDF file
 * @param file - PDF file
 * @returns Promise<number> - Total page count
 */
export const getPDFPageCount = async (file: File): Promise<number> => {
  try {
    const arrayBuffer = await file.arrayBuffer();
    const pdfDoc = await PDFDocument.load(arrayBuffer);
    return pdfDoc.getPageCount();
  } catch (error) {
    console.error('Error getting PDF page count:', error);
    throw new Error('Failed to read PDF file');
  }
};

/**
 * Validate if page range string is valid and within the PDF's page count
 * @param pageRange - String like "1-3,5,7-9"
 * @param maxPages - Maximum number of pages in the PDF (optional)
 * @returns boolean
 */
export const isValidPageRange = (pageRange: string, maxPages?: number): boolean => {
  if (!pageRange.trim()) return true; // Empty is valid (means all pages)

  try {
    const pages = parsePageRange(pageRange);
    const isValidFormat = pages.length > 0 && pages.every(page => page > 0);

    if (!isValidFormat) return false;

    // If maxPages is provided, check that all pages are within range
    if (maxPages !== undefined) {
      return pages.every(page => page <= maxPages);
    }

    return true;
  } catch {
    return false;
  }
};

/**
 * Format page range string into a human-readable format
 * Consolidates overlapping ranges and formats single pages properly
 * @param pageRange - String like "1-2,1-5,2,3,4,5" or "4-4"
 * @returns Formatted string like "1-5" or "4" for single page
 */
export const formatPageRangeForDisplay = (pageRange: string): string => {
  if (!pageRange.trim()) return '';

  try {
    // Parse the page range to get all individual pages
    const pages = parsePageRange(pageRange);

    if (pages.length === 0) return '';
    if (pages.length === 1) {
      const selectedPage = pages[0];
      return `${selectedPage}-${selectedPage}`;
    }

    // Group consecutive pages into ranges
    const ranges: string[] = [];
    let start = pages[0];
    let end = pages[0];

    for (let i = 1; i < pages.length; i++) {
      if (pages[i] === end + 1) {
        // Consecutive page, extend the current range
        end = pages[i];
      } else {
        // Non-consecutive page, finalize the current range
        if (start === end) {
          ranges.push(start.toString());
        } else {
          ranges.push(`${start}-${end}`);
        }
        start = pages[i];
        end = pages[i];
      }
    }

    // Add the final range
    if (start === end) {
      ranges.push(start.toString());
    } else {
      ranges.push(`${start}-${end}`);
    }

    return ranges.join(', ');
  } catch {
    return pageRange; // Return original if parsing fails
  }
};
